/**
 * ProtectedRoute Component
 *
 * Route protection components for wouter navigation.
 * Inlines guard logic to avoid re-creating component types on every render
 * (which would cause infinite re-render loops with useEffect + setState).
 *
 * Uses AuthContext for authentication state.
 */

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/core/contexts/AuthContext';
import { navigateAfterAuth } from '@/features/platform/auth/utils/post-auth-redirect';
import { LoadingSpinner } from '../shell/LoadingSpinner';

// ═══════════════════════════════════════════════════════════════════════
// LOADING FALLBACK
// ═══════════════════════════════════════════════════════════════════════

const LoadingFallback = () => <LoadingSpinner />;

// ═══════════════════════════════════════════════════════════════════════
// GUARD EVALUATION
// ═══════════════════════════════════════════════════════════════════════

interface GuardResult {
  allowed: boolean;
  redirectTo?: string;
  reason?: string;
}

interface GuardUser {
  id: string;
  email: string;
  role: string;
  permissions: string[];
  onboardingComplete: boolean;
  journeyInstances?: unknown[];
}

function evaluateGuards(
  isAuthenticated: boolean,
  user: GuardUser | undefined,
  options: {
    requireAuth: boolean;
    requireRole?: string[];
    requirePermissions?: string[];
    requireJourneyInstance: boolean;
    redirectTo: string;
  }
): GuardResult {
  const { requireAuth, requireRole, requirePermissions, requireJourneyInstance, redirectTo } =
    options;

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return { allowed: false, redirectTo, reason: 'User not authenticated' };
  }

  // Check role requirement
  if (requireRole && requireRole.length > 0 && user) {
    const userRole = user.role || 'member';
    if (!requireRole.includes(userRole)) {
      return {
        allowed: false,
        redirectTo: '/dashboard',
        reason: `User role '${userRole}' not in required roles: ${requireRole.join(', ')}`,
      };
    }
  }

  // Check permissions requirement
  if (requirePermissions && requirePermissions.length > 0 && user) {
    const userPermissions = user.permissions || [];
    const hasAllPermissions = requirePermissions.every((p) => userPermissions.includes(p));
    if (!hasAllPermissions) {
      return {
        allowed: false,
        redirectTo: '/dashboard',
        reason: `User missing required permissions: ${requirePermissions.join(', ')}`,
      };
    }
  }

  // Check journey instance requirement — must have at least one journey
  // the citizen can resume. Cornerstone refactor 2026-05: post-refactor
  // self-signup never lands on `'draft'` — that state is reserved for
  // invitation drafts that haven't been sent. The accessible-now bucket
  // is `active` (running) + `pending_setup` (citizen still has to
  // resolve origin/onboarding before journey-builder hands off).
  if (requireJourneyInstance && user) {
    const journeyInstances = user.journeyInstances as
      | Array<{ lifecycleState?: string }>
      | undefined;
    const hasResumableJourney =
      journeyInstances &&
      journeyInstances.some(
        (j) => j.lifecycleState === 'active' || j.lifecycleState === 'pending_setup'
      );

    if (!journeyInstances || journeyInstances.length === 0) {
      return {
        allowed: false,
        redirectTo: '/journey/builder',
        reason: 'User has no journey instance',
      };
    }

    if (!hasResumableJourney) {
      return {
        allowed: false,
        redirectTo: '/dashboard',
        reason: 'No resumable journey — all are paused, expired, completed, or cancelled',
      };
    }
  }

  return { allowed: true };
}

// ═══════════════════════════════════════════════════════════════════════
// PROTECTED ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * ProtectedRoute - Route protection wrapper
 *
 * Evaluates auth guards and redirects if conditions are not met.
 * Uses stable primitive dependencies to avoid infinite re-render loops.
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  requireRole,
  requirePermissions,
  requireJourneyInstance = false,
  redirectTo = '/login',
  fallback,
}: {
  children: ReactNode;
  requireAuth?: boolean;
  requireRole?: string[];
  requirePermissions?: string[];
  requireJourneyInstance?: boolean;
  redirectTo?: string;
  fallback?: ReactNode;
}) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [guardResult, setGuardResult] = useState<GuardResult | null>(null);
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  // Build a stable user object for guard evaluation — memoized on primitive values
  const guardUser = useMemo<GuardUser | undefined>(() => {
    if (!user) return undefined;
    return {
      id: user.id,
      email: user.email,
      role: user.role || 'talent',
      permissions: user.permissions || [],
      onboardingComplete: (user.journeyInstances && user.journeyInstances.length > 0) || false,
      journeyInstances: user.journeyInstances,
    };
  }, [user]);

  // Stabilize requireRole reference (array identity changes every render)
  const requireRoleKey = requireRole?.join(',') ?? '';
  const requirePermissionsKey = requirePermissions?.join(',') ?? '';

  useEffect(() => {
    if (isLoading) return;

    const result = evaluateGuards(isAuthenticated, guardUser, {
      requireAuth,
      requireRole,
      requirePermissions,
      requireJourneyInstance,
      redirectTo,
    });

    setGuardResult(result);

    if (!result.allowed && result.redirectTo && !hasRedirected.current) {
      hasRedirected.current = true;
      // Store current path for redirect after login (skip if user is actively
      // logging out). Include the query string + hash — otherwise an auth
      // bounce drops params like `?sponsorshipToken=…`, so the sponsorship
      // offer dialog never opens on the cold-load path (it worked only on a
      // warm reload where no bounce happens). Root cause of the intermittent
      // "click once → nothing, click again → dialog" behaviour.
      if (typeof window !== 'undefined') {
        const currentPath =
          window.location.pathname + window.location.search + window.location.hash;
        if (currentPath !== '/' && currentPath !== redirectTo) {
          sessionStorage.setItem('tonnie_redirect_after_auth', currentPath);
        }
      }
      navigate(result.redirectTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- use stable primitives, not object refs
  }, [
    isLoading,
    isAuthenticated,
    guardUser,
    requireAuth,
    requireRoleKey,
    requirePermissionsKey,
    requireJourneyInstance,
    redirectTo,
    navigate,
  ]);

  // Show loading while checking auth
  if (isLoading) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Show loading while evaluating guards (first render)
  if (guardResult === null) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Access denied
  if (!guardResult.allowed) {
    return <>{fallback || <LoadingFallback />}</>;
  }

  // Access granted
  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════════════
// PUBLIC ONLY ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * PublicOnlyRoute - For routes accessible only when NOT logged in
 */
export function PublicOnlyRoute({
  children,
  redirectTo = '/dashboard',
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      // Destination priority: an explicit `?redirect_url=` (the sponsorship-
      // offer / accept-invite email links pass it so an already-authed visitor
      // lands back on the deep link WITH its query — without this the token was
      // dropped and the offer dialog never opened), then the stored post-auth
      // path, then the default. `navigateAfterAuth` validates the param
      // (same-origin path or allow-listed host) to prevent open-redirects.
      const storedRedirect = sessionStorage.getItem('tonnie_redirect_after_auth');
      sessionStorage.removeItem('tonnie_redirect_after_auth');
      navigateAfterAuth(storedRedirect || redirectTo, navigate);
    }
  }, [isLoading, isAuthenticated, redirectTo, navigate]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (isAuthenticated) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * JourneyRequiredRoute - Convenience wrapper for routes requiring a journey instance
 */
export function JourneyRequiredRoute({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <ProtectedRoute
      requireAuth
      requireJourneyInstance
      redirectTo="/journey/builder"
      fallback={fallback}
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * AdminRoute - Convenience wrapper for admin-only routes
 */
export function AdminRoute({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <ProtectedRoute requireAuth requireRole={['admin']} redirectTo="/dashboard" fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
}
